import React, {Component} from 'react';
import {toast} from 'react-toastify';
import {withTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {isOk} from '../../../utils/apiResponse';
import {LocalStorage} from '../../../utils/LocalStorage';
import {API_URL} from '../../../settings/local_settings';
import {Loading} from '../../Loading';
import {withRouterParams} from '../../../utils/withRouterParams';
import {MERCADO_ADS_CHANNEL} from '../../channels/channels';
import {clearOauthReturn, destinationOf, errorDestinationOf, peekOauthReturn} from '../../onboarding/oauthReturn';
import {connectedDestinationFor} from './connectedDestination';
import {PublicConnectResult} from '../../connection_links/PublicConnectResult';
import {postPublicLinkCallback} from '../../connection_links/connectionLinksApi';
import {postMercadoAdsPortalCallback} from '../../channels/mercado_ads/mercadoAdsApi';
import {
    CANCELLED, ERROR_STATE_INVALID, FLOW_AGENCY, FLOW_LINK, FLOW_PORTAL, MERCADO_ADS_EXCHANGE_URL, errorResultOf,
    flowOf, redirectErrorOf, saveConnectResult, successResultOf,
} from '../../channels/mercado_ads/mercadoAds';

const AGENCY_CONNECTIONS = '/agency/conections';
const PORTAL_CONNECTIONS = '/portal/connections';
const AGENCY_USER = 'AGENCY_USER';
const PORTAL_USER = 'CLIENT_USER';
const GUARD_KEY = 'connecting_mercado_ads_oauth_code';

// El resultado que vio el vendedor con el link, por si recarga la página o vuelve con «atrás»: la URL ya no
// trae nada y sin esto vería «Cancelaste la conexión» sobre una conexión que anduvo. Se lee ANTES de inferir
// el flujo por la sesión (quien lo prueba con su sesión abierta tiene que ver lo mismo que el vendedor), y
// vence pronto: es para una recarga, no para la próxima visita.
const PUBLIC_RESULT_KEY = 'dashbo.mercado_ads.public_result';
const PUBLIC_RESULT_MAX_AGE_MS = 30 * 60 * 1000;

function rememberPublicResult(result) {
    try {
        window.sessionStorage.setItem(PUBLIC_RESULT_KEY, JSON.stringify({result, at: Date.now()}));
    } catch (e) {
        // Sin almacenamiento, recargar muestra la cancelación.
    }
}

function lastPublicResult(now = Date.now()) {
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(PUBLIC_RESULT_KEY));
        if (!parsed || !parsed.result || typeof parsed.at !== 'number' || now - parsed.at > PUBLIC_RESULT_MAX_AGE_MS) {
            return null;
        }
        return parsed.result;
    } catch (e) {
        return null;
    }
}

// Un mismo `code` se canjea una sola vez: React puede montar dos veces la misma instancia (StrictMode).
let exchangeInFlight = false;

function sessionKind() {
    if (!LocalStorage.hasUserData()) return null;
    const type = LocalStorage.fetchUserType();
    if (type === AGENCY_USER) return FLOW_AGENCY;
    if (type === PORTAL_USER) return FLOW_PORTAL;
    return null;
}

function alreadyExchanged(code) {
    try {
        return window.sessionStorage.getItem(GUARD_KEY) === code;
    } catch (e) {
        return false;
    }
}

function markExchanging(code) {
    try {
        window.sessionStorage.setItem(GUARD_KEY, code);
    } catch (e) {
        // Sin almacenamiento queda la guarda en memoria.
    }
}

function homeOf(flow) {
    return flow === FLOW_PORTAL ? PORTAL_CONNECTIONS : AGENCY_CONNECTIONS;
}

/** El destino de un éxito de la agencia: Conexiones con el acceso desplegado, o el paso que lo pidió. */
export function agencyDestination(response, tunnelReturn) {
    const credentialId = response && response.data && response.data.credential_id;
    const classic = connectedDestinationFor(MERCADO_ADS_CHANNEL, credentialId ? {data: {connection_id: credentialId}} : null);
    return destinationOf(tunnelReturn, classic);
}

/**
 * La vuelta del OAuth de Mercado Libre, la ÚNICA redirect que acepta la app (contrato §3 y §6.2). La
 * ruta es pública porque la usan tres personas distintas, y el flujo lo dice el prefijo del `state`:
 *
 * - `ag.` — la agencia. Sin sesión de agencia, al login y de vuelta acá (la URL queda guardada).
 *   Canjea en `get_access_tokens/mercado_ads/` y vuelve a Conexiones con el acceso desplegado, o al paso
 *   del asistente de líneas que pidió conectar (con su aviso, si falló).
 * - `pt.` — el cliente desde su Portal. Sin sesión de Portal, al login. Canjea en el Portal y vuelve a
 *   sus conexiones, donde elige los anunciantes si tiene más de uno.
 * - `lk.` — el vendedor con el link, sin sesión. Canjea en el endpoint público y muestra el resultado
 *   en la misma página pública, pensada para el celular.
 *
 * Lo que pasó (bien o mal) viaja a la pantalla de destino por `sessionStorage`, con la sesión que lo
 * pidió, y se dibuja en la tarjeta de la conexión: un error que pide una acción no puede ser un toast
 * que se va. Cancelar en Mercado Libre (`?error=access_denied`) es un aviso suave; cualquier otro `error`
 * de la redirect es un error (el del colaborador, si lo dice).
 *
 * Toda salida es un `replace`: la entrada del callback no queda en el historial, así que «atrás» desde
 * Conexiones no vuelve acá. Y una URL sin `code`, `error` ni `state` (una recarga) va en silencio a
 * donde corresponde: nunca dice «Cancelaste» sobre algo que no se canceló.
 */
class ConnectMercadoAdsAccount extends Component {
    constructor(props) {
        super(props);
        // `undefined` = no es el flujo del link; `null` = conectando; un objeto = el resultado.
        this.state = {publicResult: undefined};
        this.alive = true;
        // Se leen UNA vez: el montaje limpia la URL, y en desarrollo React monta dos veces la misma
        // instancia (StrictMode). Leerlos en el segundo montaje daría una vuelta sin parámetros.
        const params = new URLSearchParams(window.location.search);
        this.params = {
            code: params.get('code'), state: params.get('state'), error: params.get('error'),
            errorDescription: params.get('error_description'),
        };
    }

    componentDidMount() {
        this.alive = true;
        const {code, state, error} = this.params;
        const session = sessionKind();

        if (!code && !state && !error) {
            this.reloaded(session);
            return;
        }
        const flow = flowOf(state) || (session || (code ? FLOW_AGENCY : FLOW_LINK));
        if (flow === FLOW_LINK) {
            this.linkFlow();
            return;
        }
        if (flow !== session) {
            this.wrongSession(session);
            return;
        }
        this.cleanUrl();
        if (!code || error) {
            this.refused(flow);
            return;
        }
        // Un canje en curso (el segundo montaje de StrictMode) termina solo y navega; uno ya hecho no se
        // repite: el segundo fallaría sobre una conexión que anduvo.
        if (exchangeInFlight) return;
        if (alreadyExchanged(code)) {
            this.go(homeOf(flow));
            return;
        }
        exchangeInFlight = true;
        markExchanging(code);
        if (flow === FLOW_PORTAL) this.portalFlow(code, state);
        else this.agencyFlow(code, state);
    }

    componentWillUnmount() {
        this.alive = false;
    }

    cleanUrl() {
        try {
            window.history.replaceState(window.history.state, '', window.location.pathname);
        } catch (e) {
            // Sin History API la URL queda con el código: la guarda de arriba evita el segundo canje.
        }
    }

    go(destination) {
        this.props.history.replace(destination);
    }

    // Sin parámetros: una recarga o un «atrás». Primero el resultado del vendedor, si lo hubo en esta
    // pestaña; después, con sesión, su pantalla en silencio; sin nada, la página pública sin novedades.
    reloaded(session) {
        const previous = lastPublicResult();
        if (previous) {
            this.setState({publicResult: previous});
            return;
        }
        if (session) {
            this.go(homeOf(session));
            return;
        }
        this.setState({publicResult: {status: CANCELLED}});
    }

    // `ag.` con sesión de Portal (o al revés): el `state` es de otra persona. Sin sesión, al login y de
    // vuelta acá; con la otra sesión, a su pantalla con el aviso de que hay que volver a empezar.
    wrongSession(session) {
        if (!session) {
            LocalStorage.saveRedirectUrl(window.location.pathname + window.location.search);
            this.go('/login');
            return;
        }
        this.cleanUrl();
        saveConnectResult(session, {status: ERROR_STATE_INVALID, nickname: null});
        this.go(homeOf(session));
    }

    // Mercado Libre volvió sin código: cancelación (aviso suave) o error (a la tarjeta, o al paso del
    // asistente con su aviso).
    refused(flow) {
        const {t} = this.props;
        const code = redirectErrorOf(this.params.error, this.params.errorDescription);
        const tunnelReturn = flow === FLOW_AGENCY ? peekOauthReturn() : null;
        clearOauthReturn();
        if (code === CANCELLED) {
            toast.info(t('connections.mercado_ads.cancelled'));
            this.go(destinationOf(tunnelReturn, homeOf(flow)));
            return;
        }
        this.failedTo(flow, {status: code, nickname: null}, tunnelReturn);
    }

    // Un error de la agencia o del Portal. Al asistente vuelve por la URL (`oauth_error` + el código), que
    // es lo que dibuja ese paso, y la tarjeta de Conexiones no se guarda: nadie la leería.
    failedTo(flow, result, tunnelReturn) {
        if (tunnelReturn) {
            this.go(errorDestinationOf(tunnelReturn, homeOf(flow), MERCADO_ADS_CHANNEL, result.status));
            return;
        }
        saveConnectResult(flow, result);
        this.go(homeOf(flow));
    }

    linkFlow() {
        const {code, state, error, errorDescription} = this.params;
        this.cleanUrl();
        if (!code) {
            const result = {status: error ? redirectErrorOf(error, errorDescription) : CANCELLED};
            rememberPublicResult(result);
            this.setState({publicResult: result});
            return;
        }
        this.setState({publicResult: null});
        if (exchangeInFlight) return;
        exchangeInFlight = true;
        postPublicLinkCallback(code, state).then((response) => {
            exchangeInFlight = false;
            const result = response || {status: 'NETWORK_ERROR'};
            rememberPublicResult(result);
            if (this.alive) this.setState({publicResult: result});
        });
    }

    agencyFlow(code, state) {
        const {t} = this.props;
        // La nota del asistente de líneas (vuelve al paso que pidió conectar) se lee AL MONTAR: ver
        // `destinationOf` en `oauthReturn`.
        const tunnelReturn = peekOauthReturn();
        const failed = (response) => {
            exchangeInFlight = false;
            clearOauthReturn();
            this.failedTo(FLOW_AGENCY, errorResultOf(response), tunnelReturn);
        };
        new APIClient(API_URL + MERCADO_ADS_EXCHANGE_URL).call('POST', {code, state}).then((response) => {
            if (!isOk(response)) {
                failed(response);
                return;
            }
            exchangeInFlight = false;
            const result = successResultOf(response);
            // En Conexiones lo dice la tarjeta (con los anunciantes y el aviso de los 90 días); de vuelta al
            // asistente, donde no hay tarjeta, un toast corto.
            if (tunnelReturn) {
                const args = {nickname: result.nickname || '', interpolation: {escapeValue: false}};
                toast.success(t(result.relinked ? 'connections.mercado_ads.relinked_ok' : 'connections.mercado_ads.connected_ok', args));
            } else {
                saveConnectResult(FLOW_AGENCY, result);
            }
            this.go(agencyDestination(response, tunnelReturn));
            clearOauthReturn();
        }).catch(() => failed(null));
    }

    portalFlow(code, state) {
        const failed = (response) => {
            exchangeInFlight = false;
            this.failedTo(FLOW_PORTAL, errorResultOf(response), null);
        };
        postMercadoAdsPortalCallback(code, state).then((response) => {
            if (!isOk(response)) {
                failed(response);
                return;
            }
            exchangeInFlight = false;
            saveConnectResult(FLOW_PORTAL, successResultOf(response));
            this.go(PORTAL_CONNECTIONS);
        }).catch(() => failed(null));
    }

    render() {
        if (this.state.publicResult !== undefined) {
            return <PublicConnectResult result={this.state.publicResult}/>;
        }
        return <Loading message={this.props.t('connections.oauth.connecting')}/>;
    }
}

export default withTranslation()(withRouterParams(ConnectMercadoAdsAccount));
